import { RcFile } from "antd/es/upload";

import {
  AVERBADORAS,
  CONTRACT_TYPE,
  DOCUMENT_TYPE,
  EDUCATION_LEVEL,
  ENVELOPE_TYPE,
  KINSHIP_LEVEL,
  MARGIN_TYPE,
  OPERATION_TYPE,
  PRODUCT_TYPE,
} from "@utils/enums";

import { useApiAws } from "./base/useApiAws";
import { useApiOriginacao } from "./base/useApiOriginacao";
import { useCallback } from "react";
import { featureFlags } from "@settings";

export type IApiCriarClienteData = {
  dt_nascimento?: string;
  nu_cpf: string;
  numero_telefone: string;
  tipo_produto?: number;
  nu_beneficio?: number;
  nome_cliente: string;
  convenio?: string;
  convenio_id?: number;
  margem?: number | null;
  numero_matricula?: string;
  numero_beneficio?: string;
  folha?: string;
  folha_compra?: string;
  folha_saque?: string;
  verba?: string;
  verba_compra?: string;
  verba_saque?: string;
  tipo_margem?: MARGIN_TYPE | null;
  tipo_vinculo?: number;
  instituidor?: string;
  orgao?: string;
  classificacao_siape?: string;
  convenio_siape?: string;
  margem_compra?: number | null;
  margem_saque?: number | null;
  escolaridade?: EDUCATION_LEVEL | null;
  ocupacao?: string;
  reoffer_contract_token?: string;
  renda?: number | null;
  rogado?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL | null;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE | null;
  };
  representante_legal?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL | null;
    escolaridade?: EDUCATION_LEVEL | null;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE | null;
  };
};

export type IApiCriarClienteDataV2 = {
  nu_cpf: string;
  numero_telefone: string;
  tipo_produto: PRODUCT_TYPE;
  numero_beneficio: string | string[];
  escolaridade: EDUCATION_LEVEL | null;
  dt_nascimento?: string;
  nome_cliente?: string;
  rogado_data?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL | null;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE | null;
  };
  representante_legal?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL | null;
    escolaridade?: EDUCATION_LEVEL | null;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE | null;
  };
};

export type IApiAtualizarClienteData = {
  cd_cliente_parceiro?: string;
  onboarding?: boolean;
  cd_familiar_unico?: string;
  cliente_id: number;
  conjuge_cpf?: string;
  conjuge_data_nascimento?: string;
  conjuge_nome?: string;
  convenio?: number;
  dados_bancarios?: Array<{
    id?: number | null;
    conta_agencia?: string | null;
    conta_banco?: string | null;
    conta_digito?: string | null;
    conta_numero?: string | null;
    conta_tipo?: number | null;
    numero_beneficio?: string | null;
  }>;
  documento_data_emissao?: string | null;
  documento_numero?: string | null;
  documento_orgao_emissor?: string | null;
  documento_tipo?: number | null;
  documento_uf?: number | null;
  documentacao_cliente?: {
    tipo_documento?: number | null;
    numero_documento?: string | null;
    data_emissao?: string | null;
    orgao_emissor?: string | null;
    uf_emissor?: number | null;
  };
  dt_nascimento?: string | null;
  email?: string | null;
  endereco_bairro?: string;
  endereco_cep?: string;
  endereco_cidade?: string;
  endereco_complemento?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_residencial_tipo?: number;
  endereco_uf?: string;
  estado_civil?: string | null;
  escolaridade?: EDUCATION_LEVEL | null;
  folha?: number | string | null;
  margem_atual?: number;
  nacionalidade?: string | null;
  naturalidade?: string | null;
  nome_cliente?: string | null;
  nome_mae?: string | null;
  nome_pai?: string;
  nu_cpf?: string | null;
  numero_beneficio?: string | string[] | null;
  renda?: number | null;
  sexo?: string | null;
  telefone_celular?: string | null;
  telefone_residencial?: number;
  tipo_cliente?: number;
  tipo_logradouro?: string;
  numero_matricula?: string | null;
  contrato_id?: number;
};

type IApiCriarEnvelopeData = {
  inicio_digitacao: number;
  tipo_envelope?: ENVELOPE_TYPE;
};

interface IApiCriarContratoPhoto {
  numero_beneficio?: string;
  escolaridade?: EDUCATION_LEVEL;
  conta_tipo?: number;
  conta_banco?: string;
  conta_agencia?: string;
  conta_numero?: string;
  conta_digito?: string;
}

export type IApiCriarContratoWitness = {
  cpf?: string | null;
  nome?: string | null;
  data_nascimento?: string | null;
  documento_tipo?: DOCUMENT_TYPE | null;
};

export type IApiCriarContratoData = {
  numero_cpf?: string | null;
  tipo_produto?: PRODUCT_TYPE | null;
  tipo_contrato?: CONTRACT_TYPE | null;
  token_envelope?: string | null;
  token_contrato?: string | null;
  fl_seguro?: boolean | null;
  folha?: string | null;
  telefone_celular?: string | null;
  contrato_cross_sell?: boolean | null;
  convenio?: number | null;
  dt_desembolso?: string | null;
  dt_nascimento?: string | null;
  dt_vencimento_primeira_parcela?: string | null;
  dt_vencimento_ultima_parcela?: string | null;
  margem_atual?: number | null;
  numero_matricula?: string | null;
  numero_beneficio?: string | null;
  number_of_grace_periods?: number | null;
  contrato_digitacao_manual?: boolean | null;
  senha_servidor?: string | null;
  cet_mes?: number | null;
  cet_ano?: number | null;
  limite_pre_aprovado?: number | null;
  possui_saque?: boolean | null;
  qt_parcelas_total?: number | null;
  saque_parcelado?: boolean | null;
  taxa?: number | null;
  taxa_efetiva_mes?: number | null;
  taxa_efetiva_ano?: number | null;
  valor_disponivel_saque?: number | null;
  valor_financiado?: number | null;
  vr_contrato?: number | null;
  vr_iof?: number | null;
  vr_iof_adicional?: number | null;
  vr_iof_total?: number | null;
  vr_liberado_cliente?: number | null;
  vr_parcela?: number | null;
  vr_parcela_maxima?: number | null;
  vr_parcela_minima?: number | null;
  vr_seguro?: number | null;
  valor_saque?: number | null;
  vencimento_fatura?: string | null;
  verba?: string | null;
  anexo_cnh?: RcFile;

  // Cartão
  averbadora?: number | null;
  id_cliente_cartao?: number | null;
  tipo_margem?: MARGIN_TYPE | null;
  verba_compra?: string | null;
  verba_saque?: string | null;
  folha_compra?: string | null;
  folha_saque?: string | null;
  cartoes?: Array<CardRawData> | null;

  // Campos de Portabilidade+Refinanciamento
  id_cliente?: number | null;
  id_rogado?: string | null;
  escolaridade?: EDUCATION_LEVEL | null;
  identificador_usuario?: string | null;
  portabilidade_refinanciamento?: Array<
    PayloadWithInsurance<JourneyDataPortabilityRefinContract>
  >;
  margin_increase?: Array<JourneyDataMarginIncreaseContract>;
  // Contratos portabilidade
  portabilidade?: Array<{
    key?: number;
    numero_contrato?: string;
    banco?: string;
    taxa?: number;
    saldo_devedor?: number;
    prazo?: number;
    parcela_digitada?: number;
    nova_parcela?: number;
    cet_ano?: number;
    saved?: boolean;
  }>;
  // Contratos de refinanciamento
  refinanciamentos?:
    | Array<PayloadWithInsurance<JourneyDataRefinancingSelectedContractItem>>
    | JourneyDataRefinancingCef["selectedContracts"]
    | JourneyDataRefinancingCef["selectedContractsV2"];
  consig_privado?: {
    vinculo_empregaticio?: WorksContractsDataprev;
    conta_bancaria?: AccountsBank;
    simulacao?: PrivateConsigCefSimulationResult;
  };
  documentos_reaproveitados_ids?: number[];
  // TODO: Remover quando excluir a flag enableRefinancingCefSimulationV2
  dados_bancarios_refin?: Record<string, RefinancingBankData[]>;
  testemunhas?: Array<IApiCriarContratoWitness | null | undefined>;

  // Contratos Receba
  journey_token?: string | null;
  proposal_key?: string | null;
  offer_key?: string | null;
  declared_gross_benefit_amount?: number | null; // renda / Mens. Reajustada (MR)
  descontos_obrigatorios?: number | null; // Descontos Obrigatórios
  debitos_concorrentes?: number | null; // Débitos Concorrentes
  interest_base?: number | null; // interest_base
  min_first_installment_grace_period?: number | null; // Carencia min
  max_first_installment_grace_period?: number | null; // Carencia nax
  due_date_rule?: string | null; // Regra de pagamento
  calculated_net_benefit_value?: number | null; // Base de Cálculo
  commitment_ratio_percentage?: number | null; // % de Comprometimento
  max_commitment_percentage?: number | null; //
  vr_tac?: number | null; //
  foto_contrato?: Array<IApiCriarContratoPhoto>;
  multi_products?: IApiCriarContratoMultiProducts;
};

export type IApiMultiproductPortRefinancingContract = {
  key?: string;
  contrato_id?: string;
  banco?: string;
  contrato?: string;
  saldo_devedor?: number;
  parcela?: number;
  prazo?: number;
  prazo_contratado?: number;
  parcelas_atrasadas?: number;
  nova_parcela?: number;
  nova_taxa?: number;
  novo_prazo?: number;
  valor_parcela_ajustada?: number;
  taxa_ajustada?: number;
  resultado_recalculo_parcela?: number[];
  number_of_grace_periods?: number | null;
  margem_liberada?: number;
  troco?: number;
  valor_operacao?: number;
  cet_ano?: number;
  plano?: unknown;
  numero_beneficio: string;
};

export type IApiMultiproductPortabilityContract = {
  key?: string;
  contrato_id?: string;
  numero_contrato?: string;
  banco?: string;
  taxa?: number;
  saldo_devedor?: number;
  prazo?: number;
  parcela_digitada?: number;
  prazo_contratado?: number;
  parcelas_atrasadas?: number;
  nova_parcela?: number;
  cet_ano?: number;
  numero_beneficio: string;
};

export type IApiMultiproductCommonContractData = {
  id_cliente?: number | null;
  id_rogado?: string | null;
  numero_beneficio?: string | null;
  identificador_usuario?: string | null;
  tipo_produto?: PRODUCT_TYPE | null;
  escolaridade?: EDUCATION_LEVEL | null;
  foto_contrato?: Array<IApiCriarContratoPhoto>;
  tipo_contrato?: CONTRACT_TYPE | null;
  token_envelope?: string | null;
  numero_cpf?: string | null;
  contrato_cross_sell?: boolean | null;
  testemunhas?: IApiCriarContratoWitness[];
};

export type IApiMultiproductMarginContractData =
  IApiMultiproductCommonContractData & {
    telefone_celular?: string | null;
    dt_nascimento?: string | null;
    numero_beneficio: string;
    vr_parcela_maxima?: number | null;
    vr_parcela_minima?: number | null;
    qt_parcelas_total?: number | null;
    taxa?: number | null;
    taxa_efetiva_mes?: number | null;
    taxa_efetiva_ano?: number | null;
    cet_mes?: number | null;
    cet_ano?: number | null;
    vr_liberado_cliente?: number | null;
    vr_parcela?: number | null;
    vr_contrato?: number | null;
    vr_iof?: number | null;
    vr_iof_adicional?: number | null;
    dt_vencimento_primeira_parcela?: string | null;
    dt_vencimento_ultima_parcela?: string | null;
    fl_seguro?: boolean | null;
    vr_seguro?: number | null;
    dt_desembolso?: string | null;
    plano?: unknown;
    number_of_grace_periods?: number | null;
  };

export type IApiCriarContratoMultiProducts = {
  margem_livre_contracts?: IApiMultiproductMarginContractData[];
  port_refin_contracts?: Array<
    IApiMultiproductCommonContractData & IApiMultiproductPortRefinancingContract
  >;
  portabilidade_contracts?: Array<
    IApiMultiproductCommonContractData & IApiMultiproductPortabilityContract
  >;
};

type IApiBuscarCEPData = {
  cep: string;
};

type IApiEnvioDocumentosData = FormData /* {
  tipo_anexo?: number;
  arquivo: any;
  nome_anexo: string;
  anexo_extensao: string;
  token_contrato: string;
  cliente: number;
  contrato_id: number;
} */;

/**
 * {
 *  image-validation: string;
 *  file-type: string;
 *  contract_id: number;
 * }
 * */
export type IApiValidaDocumentosData = {
  "image-validation": string;
  "file-type": string;
  contract_id: number;
};

export type IApiCriarClienteResponse = {
  dt_nascimento: string;
  id: number;
  nu_cpf: string;
  cliente_cartao_beneficio_id?: number;
  rogado: {
    id: string;
    nome: string;
    telefone: string;
    cpf: string;
    grau_parentesco: KINSHIP_LEVEL;
    data_nascimento: string;
  };
  rogado_data?: {
    id: string;
    nome: string;
    telefone: string;
    cpf: string;
    grau_parentesco: KINSHIP_LEVEL;
    data_nascimento: string;
  };
  representante_legal?: {
    id: string;
  };
};

export type IApiAtualizarClienteResponse = {
  msg: string;
};

export type IApiCriarEnvelopeResponse = {
  token_envelope: string;
};

type CriarContratoWitness = {
  id: string;
  cliente_id: string;
  nome: string;
  cpf: string;
  data_nascimento: string;
  documento_tipo: string;
};

type CriarContratoBaseResponse = {
  chave_proposta: string | null;
  cliente: {
    dt_nascimento: string;
    id: number;
    nome_cliente: string;
    nu_cpf: string;
  };
  id: number;
  necessita_assinatura_fisica?: boolean;
  testemunhas: CriarContratoWitness[];
  token_contrato: string;
};

type CriarContratoCardResponse = CriarContratoBaseResponse[];

type CriarContratoPortResponse = {
  contract_ids: number[];
  contract_tokens: string[];
  portability_ids: number[];
  testemunhas: CriarContratoWitness[];
  necessita_assinatura_fisica: boolean[];
};

type CriarContratoPortRefinResponse = CriarContratoPortResponse & {
  refinancing_ids: number[];
};

export type IApiCriarContratoResponse =
  | CriarContratoBaseResponse
  | CriarContratoCardResponse
  | CriarContratoPortResponse
  | CriarContratoPortRefinResponse;

export type IApiBancosResponse = Array<{
  codigo: string;
  id: number;
  nome_: string;
  ispb: string;
}>;

export type IApiBancosData = {
  cd_produto: string;
};

export type IApiBancosMultiProductData = {
  tipo_operacao: OPERATION_TYPE;
  cd_produto: string[];
};

export type IApiTaxaResponse = Array<{
  ativo: boolean;
  id: number;
  taxa: string;
  vr_max: string;
  vr_min: string;
  transbordo?: boolean;
}>;

export type IApiTiposdeContaBancariaResponse = Array<{
  id: number;
  tipo_conta: string;
}>;

export type IApiListarDocumentosResponse = Array<{
  id: DOCUMENT_TYPE;
  documento: string;
}>;

export type IApiListarUfsResponse = Array<{
  estado: string;
  id: number;
}>;
export type IApiListarMunicipiosResponse = {
  estado: string;
  quantidade: number;
  municipios: string[];
};

export type IApiListarSexosResponse = Array<{
  id: number;
  sexo: string;
}>;

type IApiTermosUsoResponse = {
  count: number;
  next: number | null;
  previous: number | null;
  results: Array<{
    termos_de_uso: string;
    politica_privacidade: string;
  }>;
};

export type IApiBuscarCEPResponse = {
  bairro: string;
  cep: string;
  localidade: string;
  complemento: string;
  logradouro: string;
  unidade?: string;
  uf: string;
  estado?: string;
  regiao?: string;
  ibge?: string;
  gia?: string;
  ddd?: string;
  siafi?: string;
  erro?: string;
  msg?: string;
};

type IApiEnvioDocumentosResponse = {
  anexado_em: string;
  anexo_extensao: string;
  anexo_url: string;
  contrato: number;
  nome_anexo: string;
  tipo_anexo: number;
};

export type IApiParametroProdutoResponse = {
  id: number;
  nome: string;
  cd_produto: string;
  documento_pessoal: boolean;
  comprovante_residencia: boolean;
  contracheque: boolean;
  ativo: boolean;
};

export type IApiValidaDocumentosResponse = {
  message: string;
  status: boolean;
  signature: string;
};

export type IApiParametroProdutoData = {
  tipo_produto: PRODUCT_TYPE;
  convenio?: number;
  created_by: number;
  cliente_id?: number;
  endereco: {
    cep: boolean;
    logradouro: boolean;
    numero: boolean;
    bairro: boolean;
    cidade: boolean;
    uf: boolean;
    complemento: boolean;
  };
};

type IApiValidarTelefoneData = {
  telefone_celular: string;
  tipo_produto: PRODUCT_TYPE;
};

export type AvailableOfferProduct<T = { tipo_produto: PRODUCT_TYPE }> =
  T extends {
    tipo_produto:
      | PRODUCT_TYPE.CARTAO_BENEFICIO
      | PRODUCT_TYPE.CARTAO_CONSIGNADO;
  }
    ? {
        margem_atual: number;
        convenio: number;
        folha: number;
        numero_matricula: string;
        averbadora: AVERBADORAS;
        necessita_assinatura_fisica: boolean;
        idade_minima_assinatura: number;
        tipo_margem: MARGIN_TYPE;
        tipo_produto:
          | PRODUCT_TYPE.CARTAO_BENEFICIO
          | PRODUCT_TYPE.CARTAO_CONSIGNADO;
      }
    : { tipo_produto: PRODUCT_TYPE };

export type IApiAvailableOffersResponse = {
  produtos: Array<AvailableOfferProduct>;
};

export type IApiProductParamsResponse = Array<{
  tipoProduto: PRODUCT_TYPE;
  permite_representante_legal: boolean;
  idade_minima: number;
  idade_maxima: number;
  idade_minima_cliente_com_representante_legal: number;
  idade_maxima_cliente_com_representante_legal: number;
  idade_minima_do_representante_legal: number;
  idade_maxima_do_representante_legal: number;
}>;

type IApiUpdateLegalRepresentativeData = {
  documento_tipo_input: DOCUMENT_TYPE;
  documento_numero: string;
  documento_uf: number;
  documento_orgao_emissor: string;
  documento_data_emissao: string;
  email: string;
};

export type IApiBureauCadastralResponse =
  | {
      permite_digitacao: true;
      mensagem: string;
      grupo_dados_pessoais: {
        endereco?: string[];
        endereco_json?: Array<{
          cep: number;
          cidade: string;
          municipio: string;
          numero: string;
          rua: string;
          uf: string | null;
          complemento: string | null;
        }>;
        emails?: string[];
        nome_da_mae?: string;
      };
    }
  | {
      permite_digitacao: false;
      mensagem: string;
      grupo_dados_pessoais: undefined;
    };

type IApiBureauCadastralData = {
  nu_cpf: string;
  produto: number;
  convenio?: number;
  canal: string;
  corban: number;
  loja: number;
};

type IApiValidateEspecieAndCpfResponse = {
  is_valid: boolean;
};

type IApiConfirmarTermosResponse = {
  msg: string;
};

type IApiConfirmarTermosData = {
  id: number;
  termo_uso_privacidade: boolean;
};

export const useApiCriarCliente = () => {
  return useApiOriginacao<IApiCriarClienteResponse, IApiCriarClienteData>({
    method: "POST",
    url: "/criar-cliente/",
  });
};

export const useApiCriarClienteV2 = () => {
  return useApiOriginacao<IApiCriarClienteResponse, IApiCriarClienteDataV2>({
    method: "POST",
    url: "/criar-cliente-simplificado/",
  });
};

export const useApiAtualizarCliente = () => {
  return useApiOriginacao<
    IApiAtualizarClienteResponse,
    IApiAtualizarClienteData
  >({
    method: "PATCH",
    url: "/atualizar-cliente/",
    headers: { Accept: "application/json; version=2" },
  });
};

export const useApiValidarTelefone = () => {
  return useApiOriginacao<IApiValidarTelefoneData>({
    method: "POST",
    url: "/validar-telefone/",
  });
};

export const useApiCriarEnvelope = () => {
  return useApiOriginacao<IApiCriarEnvelopeResponse, IApiCriarEnvelopeData>({
    method: "POST",
    url: "/criar-envelope/",
    headers: { Accept: "application/json; version=2" },
  });
};

export const useApiCriarContrato = () => {
  return useApiOriginacao<IApiCriarContratoResponse, IApiCriarContratoData>({
    method: "POST",
    url: "/criar-contrato/",
  });
};

export const useApiBancos = () => {
  const isGetRequest = featureFlags.enableGetApiBank;

  const [fetchBancos, { data, loading }] = useApiOriginacao<IApiBancosResponse>(
    {},
  );

  const getMethodBancos = useCallback(
    (params: { tipo_operacao: number; cd_produto: string }) => {
      return fetchBancos({
        method: "GET",
        url: `/bancos/${params.tipo_operacao}/${params.cd_produto}/`,
      });
    },
    [fetchBancos],
  );

  const postMethodBancos = useCallback(
    (payload?: IApiBancosData) => {
      return fetchBancos({
        method: "POST",
        url: "/bancos/",
        data: payload,
      });
    },
    [fetchBancos],
  );

  const getBancos = useCallback(
    (params?: { tipo_operacao?: number; cd_produto?: string }) => {
      if (params?.tipo_operacao && params?.cd_produto) {
        return getMethodBancos({
          tipo_operacao: params.tipo_operacao,
          cd_produto: params.cd_produto,
        });
      }

      if (isGetRequest) {
        return fetchBancos({
          method: "GET",
          url: "/bancos/",
        });
      }

      return postMethodBancos(params as IApiBancosData);
    },
    [isGetRequest, getMethodBancos, postMethodBancos, fetchBancos],
  );

  const getBancosMultiProduct = useCallback(
    (payload: IApiBancosMultiProductData) => {
      return fetchBancos({
        method: "POST",
        url: "/bancos/",
        data: payload,
      });
    },
    [fetchBancos],
  );

  return [
    getBancos,
    { banks: data, banksLoading: loading, getBancosMultiProduct },
  ] as const;
};

export const useApiTaxa = () => {
  return useApiOriginacao<IApiTaxaResponse>({
    method: "GET",
    url: "/taxa/",
  });
};

export const useApiTiposDeContaBancaria = () => {
  return useApiOriginacao<IApiTiposdeContaBancariaResponse>({
    method: "GET",
    url: "/tipos-de-conta-bancaria/",
  });
};

export const useApiListarDocumentos = () => {
  return useApiOriginacao<IApiListarDocumentosResponse>({
    method: "GET",
    url: "/listar-documentos/",
  });
};

export const useApiListarUfs = () => {
  return useApiOriginacao<IApiListarUfsResponse>({
    method: "GET",
    url: "/listar-ufs/",
  });
};

export const useApiListarMunicipios = () => {
  const [_fetchMunicipios, state] =
    useApiOriginacao<IApiListarMunicipiosResponse>({
      method: "GET",
    });

  const fetchMunicipios = useCallback(
    (uf: string) => _fetchMunicipios({ url: `/municipios/${uf}/` }),
    [_fetchMunicipios],
  );

  return [fetchMunicipios, state] as const;
};

export const useApiListarSexos = () => {
  return useApiOriginacao<IApiListarSexosResponse>({
    method: "GET",
    url: "/listar-sexos/",
  });
};

export const useApiTermosUso = () => {
  return useApiOriginacao<IApiTermosUsoResponse>({
    method: "GET",
    url: "/termos-de-uso/",
  });
};

export const useApiConfirmarTermos = () => {
  return useApiOriginacao<IApiConfirmarTermosResponse, IApiConfirmarTermosData>(
    {
      method: "PATCH",
      url: "/auth/update-termo-uso/",
    },
  );
};

export const useApiBuscarCEP = () => {
  return useApiOriginacao<IApiBuscarCEPResponse, IApiBuscarCEPData>({
    method: "POST",
    url: "/buscar-cep/",
  });
};

export const useApiEnvioDocumentos = () => {
  return useApiOriginacao<IApiEnvioDocumentosResponse, IApiEnvioDocumentosData>(
    {
      method: "POST",
      url: "/envio-documentos/",
    },
  );
};

export const useApiParametroProduto = () => {
  return useApiOriginacao<
    IApiParametroProdutoResponse,
    IApiParametroProdutoData
  >({
    method: "POST",
    url: "/contratos/consulta-documentos-produto/",
  });
};

export const useApiAvailableOffers = () => {
  return useApiOriginacao<IApiAvailableOffersResponse>({
    method: "GET",
    url: "/available-offers/",
  });
};

export const useApiProductParams = () => {
  return useApiOriginacao<IApiProductParamsResponse>({
    method: "GET",
  });
};

export const useApiUpdateLegalRepresentative = () => {
  return useApiOriginacao<IApiUpdateLegalRepresentativeData>({
    method: "PATCH",
  });
};

export const useApiBureauCadastral = () => {
  return useApiOriginacao<IApiBureauCadastralResponse, IApiBureauCadastralData>(
    {
      method: "POST",
      url: "/bureau-cadastral/",
    },
  );
};

export const useApiValidateEspecieAndCpf = () => {
  return useApiOriginacao<IApiValidateEspecieAndCpfResponse>({
    method: "GET",
  });
};

export const useApiValidaDocumentos = () => {
  return useApiAws<IApiValidaDocumentosResponse, IApiValidaDocumentosData>({
    method: "POST",
    url: "/imagevalidation",
  });
};
